/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Financial Business Intelligence & Real-Time Gross Margin Dashboard (Task 6.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  DollarSign,
  PieChart as PieChartIcon,
  Cpu,
  Server,
  Gift,
  RefreshCw,
  Download,
  CheckCircle2,
  ArrowUpRight,
  CreditCard,
  Loader2,
} from 'lucide-react';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
} from 'recharts';
import { useI18n } from '../i18n';
import { adminClient } from '../lib/api-client';
import { exportToCsv } from '../components/table/DataTable';
import type {
  AdminAnalyticsPeriod,
  AdminFinancialReportResponse,
} from '@sofia/shared-types';

const USD_TO_VND_RATE = 25400;

const DONUT_COLORS = ['#06b6d4', '#10b981', '#f59e0b', '#0ea5e9'];
const PROVIDER_COLORS = ['#f43f5e', '#10b981', '#f59e0b', '#06b6d4'];

export const FinancialManagementView: React.FC = () => {
  const { t } = useI18n();
  const [period, setPeriod] = useState<AdminAnalyticsPeriod>('30d');
  const [showVnd, setShowVnd] = useState<boolean>(false);

  // Fetch Financial BI Report from API Worker
  const { data, isLoading, isFetching, refetch } = useQuery<{
    success: boolean;
    data: AdminFinancialReportResponse;
  }>({
    queryKey: ['admin-financial-report', period],
    queryFn: async () => {
      return adminClient.get('/api/v1/admin/analytics/financial', { period });
    },
  });

  const report = data?.data;
  const summary = report?.summary;
  const timeline = report?.timeline || [];
  const aiProviderCosts = report?.aiProviderCosts || [];
  const cloudflareCosts = report?.cloudflareCosts;
  const gatewayShares = report?.gatewayShares || [];
  const promotionalGrants = report?.promotionalGrants || [];

  // Format currency helper
  const formatMoney = (usdVal: number) => {
    if (showVnd) {
      const vnd = Math.round(usdVal * USD_TO_VND_RATE);
      return `${vnd.toLocaleString('vi-VN')} ₫`;
    }
    return `$${usdVal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Export report to CSV
  const handleExportCsv = () => {
    if (!timeline.length) return;
    const exportData = timeline.map((pt) => ({
      Date: pt.date,
      'Subscription Revenue (USD)': pt.subscriptionRevenue,
      'Token Pack Revenue (USD)': pt.tokenPackRevenue,
      'Total Revenue (USD)': pt.totalRevenue,
      'Total Revenue (VND)': Math.round(pt.totalRevenue * USD_TO_VND_RATE),
      'AI Provider Cost (USD)': pt.aiCost,
      'Cloudflare Infra Cost (USD)': pt.infraCost,
      'Total COGS (USD)': pt.totalCogs,
      'Gross Profit (USD)': pt.grossProfit,
      'Gross Margin (%)': pt.grossMarginPct,
    }));

    exportToCsv(
      [
        { id: 'Date', header: 'Date', accessorKey: 'Date' as any },
        { id: 'Subscription Revenue (USD)', header: 'Subscription Revenue (USD)', accessorKey: 'Subscription Revenue (USD)' as any },
        { id: 'Token Pack Revenue (USD)', header: 'Token Pack Revenue (USD)', accessorKey: 'Token Pack Revenue (USD)' as any },
        { id: 'Total Revenue (USD)', header: 'Total Revenue (USD)', accessorKey: 'Total Revenue (USD)' as any },
        { id: 'Total Revenue (VND)', header: 'Total Revenue (VND)', accessorKey: 'Total Revenue (VND)' as any },
        { id: 'AI Provider Cost (USD)', header: 'AI Provider Cost (USD)', accessorKey: 'AI Provider Cost (USD)' as any },
        { id: 'Cloudflare Infra Cost (USD)', header: 'Cloudflare Infra Cost (USD)', accessorKey: 'Cloudflare Infra Cost (USD)' as any },
        { id: 'Total COGS (USD)', header: 'Total COGS (USD)', accessorKey: 'Total COGS (USD)' as any },
        { id: 'Gross Profit (USD)', header: 'Gross Profit (USD)', accessorKey: 'Gross Profit (USD)' as any },
        { id: 'Gross Margin (%)', header: 'Gross Margin (%)', accessorKey: 'Gross Margin (%)' as any },
      ],
      exportData as any,
      `sophia-financial-bi-${period}-${new Date().toISOString().slice(0, 10)}.csv`
    );
  };

  // Revenue composition pie data
  const revenueCompositionData = [
    {
      name: t('admin.financial.legendSub'),
      value: summary?.subscriptionRevenue || 0,
      color: '#06b6d4',
    },
    {
      name: t('admin.financial.legendTopup'),
      value: summary?.tokenPackRevenue || 0,
      color: '#f59e0b',
    },
  ];

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-20 space-y-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <span className="text-xs text-foreground-muted">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12 max-w-7xl mx-auto">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-surface-border">
        <div>
          <h1 className="text-xl font-heading font-bold text-foreground flex items-center gap-2.5">
            <TrendingUp className="w-6 h-6 text-primary" />
            {t('admin.financial.title')}
          </h1>
          <p className="text-xs text-foreground-muted mt-1">
            {t('admin.financial.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Currency Toggle */}
          <button
            type="button"
            onClick={() => setShowVnd(!showVnd)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-surface-border bg-surface text-foreground text-xs font-semibold hover:bg-muted transition-colors shadow-sm"
          >
            <DollarSign className="w-3.5 h-3.5 text-primary" />
            <span>{showVnd ? 'VND (₫)' : 'USD ($)'}</span>
          </button>

          {/* Period Selector */}
          <div className="flex items-center rounded-xl border border-surface-border bg-surface p-1 shadow-sm text-xs font-medium">
            {(['7d', '30d', '90d', '12m'] as AdminAnalyticsPeriod[]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={`px-3 py-1 rounded-lg transition-all ${
                  period === p
                    ? 'bg-primary text-primary-fg font-bold shadow-sm'
                    : 'text-foreground-muted hover:text-foreground'
                }`}
              >
                {p}
              </button>
            ))}
          </div>

          {/* Refresh */}
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-surface-border bg-surface text-foreground hover:bg-muted text-xs font-medium transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            <span>{t('common.refresh')}</span>
          </button>

          {/* Export CSV */}
          <button
            type="button"
            onClick={handleExportCsv}
            className="flex items-center gap-2 px-4 py-1.5 rounded-xl bg-primary text-primary-fg hover:bg-primary-hover active:scale-[0.98] text-xs font-semibold shadow-md transition-all"
          >
            <Download className="w-3.5 h-3.5" />
            <span>{t('admin.financial.exportReport')}</span>
          </button>
        </div>
      </div>

      {/* 4 Core Financial KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* KPI 1: Gross Revenue */}
        <div className="p-5 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-2">
          <div className="flex items-center justify-between text-foreground-muted">
            <span className="text-xs font-medium">{t('admin.financial.kpiGrossRevenue')}</span>
            <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-heading font-bold text-foreground">
              {formatMoney(summary?.totalRevenue || 0)}
            </span>
          </div>
          <div className="text-[11px] text-foreground-muted flex items-center justify-between pt-1 border-t border-surface-border/60">
            <span>MRR: <strong className="text-foreground">{formatMoney(summary?.mrr || 0)}</strong></span>
            <span>ARR: <strong className="text-foreground">{formatMoney(summary?.arr || 0)}</strong></span>
          </div>
        </div>

        {/* KPI 2: Total COGS */}
        <div className="p-5 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-2">
          <div className="flex items-center justify-between text-foreground-muted">
            <span className="text-xs font-medium">{t('admin.financial.kpiTotalCogs')}</span>
            <div className="w-8 h-8 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-500 flex items-center justify-center">
              <Cpu className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-heading font-bold text-rose-500">
              {formatMoney(summary?.totalCogs || 0)}
            </span>
          </div>
          <div className="text-[11px] text-foreground-muted flex items-center justify-between pt-1 border-t border-surface-border/60">
            <span>AI APIs: <strong className="text-foreground">{formatMoney(summary?.aiProviderCost || 0)}</strong></span>
            <span>Edge: <strong className="text-foreground">{formatMoney(summary?.cloudflareInfraCost || 0)}</strong></span>
          </div>
        </div>

        {/* KPI 3: Net Gross Profit */}
        <div className="p-5 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-2">
          <div className="flex items-center justify-between text-foreground-muted">
            <span className="text-xs font-medium">{t('admin.financial.kpiGrossProfit')}</span>
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-heading font-bold text-emerald-500">
              {formatMoney(summary?.grossProfit || 0)}
            </span>
          </div>
          <div className="text-[11px] text-emerald-500 font-semibold flex items-center gap-1 pt-1 border-t border-surface-border/60">
            <ArrowUpRight className="w-3.5 h-3.5" />
            <span>{summary?.grossMarginPct || 0}% {t('admin.financial.kpiGrossMargin')}</span>
          </div>
        </div>

        {/* KPI 4: Promotional Credit Grants */}
        <div className="p-5 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-2">
          <div className="flex items-center justify-between text-foreground-muted">
            <span className="text-xs font-medium">{t('admin.financial.kpiPromotional')}</span>
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-500 flex items-center justify-center">
              <Gift className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-heading font-bold text-foreground">
              {(summary?.promotionalCreditsGranted || 0).toLocaleString()}
            </span>
            <span className="text-[10px] text-foreground-muted font-mono">credits</span>
          </div>
          <div className="text-[11px] text-foreground-muted flex items-center justify-between pt-1 border-t border-surface-border/60">
            <span>Liability:</span>
            <span className="font-mono font-semibold text-foreground">
              {formatMoney(summary?.promotionalCreditsDollarValue || 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Main Recharts Composed Chart: Revenue vs COGS vs Gross Margin % */}
      <div className="p-6 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-surface-border pb-4">
          <div>
            <h3 className="text-sm font-heading font-bold text-foreground flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              {t('admin.financial.chartRevenueTimeline')}
            </h3>
            <p className="text-[11px] text-foreground-muted">
              {t('admin.financial.kpiGrossProfitSub')}
            </p>
          </div>
          <div className="flex items-center gap-4 text-xs font-medium">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-primary" />
              <span>{t('admin.financial.legendRevenue')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-rose-500" />
              <span>{t('admin.financial.legendCogs')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 bg-emerald-500" />
              <span>{t('admin.financial.legendMargin')}</span>
            </div>
          </div>
        </div>

        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={timeline} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
              <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickLine={false} />
              <YAxis
                yAxisId="left"
                stroke="#94a3b8"
                fontSize={11}
                tickFormatter={(v) => `$${v}`}
                tickLine={false}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#10b981"
                fontSize={11}
                domain={[50, 100]}
                tickFormatter={(v) => `${v}%`}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0f172a',
                  borderColor: '#334155',
                  borderRadius: '12px',
                  fontSize: '12px',
                  color: '#f8fafc',
                }}
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="totalRevenue"
                fill="#06b6d4"
                fillOpacity={0.15}
                stroke="#06b6d4"
                strokeWidth={2}
                name={t('admin.financial.legendRevenue')}
              />
              <Bar
                yAxisId="left"
                dataKey="totalCogs"
                fill="#f43f5e"
                radius={[4, 4, 0, 0]}
                maxBarSize={28}
                name={t('admin.financial.legendCogs')}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="grossMarginPct"
                stroke="#10b981"
                strokeWidth={2.5}
                dot={{ fill: '#10b981', r: 3 }}
                name={t('admin.financial.legendMargin')}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Secondary Charts: Revenue Composition & Gateway Volume & AI Costs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Composition Donut */}
        <div className="p-6 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-4 flex flex-col justify-between">
          <div>
            <h3 className="text-sm font-heading font-bold text-foreground flex items-center gap-2">
              <PieChartIcon className="w-4 h-4 text-primary" />
              {t('admin.financial.chartRevenueComposition')}
            </h3>
            <p className="text-[11px] text-foreground-muted">
              {t('admin.financial.kpiGrossRevenueSub')}
            </p>
          </div>

          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={revenueCompositionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {revenueCompositionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(val: any) => formatMoney(Number(val) || 0)}
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    borderColor: '#334155',
                    borderRadius: '12px',
                    fontSize: '12px',
                    color: '#f8fafc',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-surface-border text-xs">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-primary" />
              <div>
                <span className="text-[10px] text-foreground-muted block">Subscriptions</span>
                <span className="font-semibold text-foreground">{formatMoney(summary?.subscriptionRevenue || 0)}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-amber-500" />
              <div>
                <span className="text-[10px] text-foreground-muted block">Token Packs</span>
                <span className="font-semibold text-foreground">{formatMoney(summary?.tokenPackRevenue || 0)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* AI Provider Costs Bar Chart */}
        <div className="p-6 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-4 flex flex-col justify-between">
          <div>
            <h3 className="text-sm font-heading font-bold text-foreground flex items-center gap-2">
              <Cpu className="w-4 h-4 text-rose-500" />
              {t('admin.financial.chartAiProviderCosts')}
            </h3>
            <p className="text-[11px] text-foreground-muted">
              Anthropic, OpenAI, Google Gemini & DeepSeek
            </p>
          </div>

          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={aiProviderCosts} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} opacity={0.2} />
                <XAxis type="number" stroke="#94a3b8" fontSize={10} tickFormatter={(v) => `$${v}`} />
                <YAxis dataKey="provider" type="category" stroke="#94a3b8" fontSize={11} width={65} />
                <Tooltip
                  formatter={(val: any) => formatMoney(Number(val) || 0)}
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    borderColor: '#334155',
                    borderRadius: '12px',
                    fontSize: '12px',
                    color: '#f8fafc',
                  }}
                />
                <Bar dataKey="estimatedCost" fill="#f43f5e" radius={[0, 4, 4, 0]}>
                  {aiProviderCosts.map((_, i) => (
                    <Cell key={`bar-${i}`} fill={PROVIDER_COLORS[i % PROVIDER_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="pt-3 border-t border-surface-border flex items-center justify-between text-xs">
            <span className="text-foreground-muted">Total AI COGS:</span>
            <span className="font-mono font-bold text-rose-500">
              {formatMoney(summary?.aiProviderCost || 0)}
            </span>
          </div>
        </div>

        {/* Payment Gateways Volume Share */}
        <div className="p-6 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-4 flex flex-col justify-between">
          <div>
            <h3 className="text-sm font-heading font-bold text-foreground flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-emerald-500" />
              {t('admin.financial.chartGatewaySplit')}
            </h3>
            <p className="text-[11px] text-foreground-muted">
              Stripe, PayPal, Paddle MoR & Bank Wire
            </p>
          </div>

          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={gatewayShares}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="volumeUsd"
                  nameKey="gatewayName"
                >
                  {gatewayShares.map((_, idx) => (
                    <Cell key={`gw-cell-${idx}`} fill={DONUT_COLORS[idx % DONUT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(val: any) => formatMoney(Number(val) || 0)}
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    borderColor: '#334155',
                    borderRadius: '12px',
                    fontSize: '12px',
                    color: '#f8fafc',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-3 border-t border-surface-border text-[11px]">
            {gatewayShares.map((gw, idx) => (
              <div key={gw.gatewayCode} className="flex items-center gap-1.5 truncate">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: DONUT_COLORS[idx % DONUT_COLORS.length] }}
                />
                <span className="text-foreground truncate">{gw.gatewayName}</span>
                <span className="font-mono text-foreground-muted ml-auto font-semibold">
                  {gw.sharePct}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cloudflare Edge Infrastructure Cost Matrix */}
      {cloudflareCosts && (
        <div className="p-6 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-surface-border pb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center">
                <Server className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-heading font-bold text-foreground">
                  {t('admin.financial.chartCloudflareInfra')}
                </h3>
                <p className="text-[11px] text-foreground-muted">
                  Edge compute, relational D1 queries, object R2 storage, and Vectorize indexes
                </p>
              </div>
            </div>

            <div className="text-right">
              <span className="text-[10px] text-foreground-muted block">Total Edge Infra</span>
              <span className="font-mono text-sm font-bold text-foreground">
                {formatMoney(cloudflareCosts.totalInfraCost)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
            <div className="p-3.5 rounded-xl border border-surface-border bg-muted/20 space-y-1">
              <span className="text-[10px] text-foreground-muted block">Workers Compute</span>
              <span className="font-mono font-bold text-foreground block">
                {(cloudflareCosts.workersRequests / 1000000).toFixed(1)}M requests
              </span>
              <span className="text-[11px] text-primary font-semibold font-mono">
                {formatMoney(cloudflareCosts.workersCost)}
              </span>
            </div>

            <div className="p-3.5 rounded-xl border border-surface-border bg-muted/20 space-y-1">
              <span className="text-[10px] text-foreground-muted block">D1 Database (SQL)</span>
              <span className="font-mono font-bold text-foreground block">
                {(cloudflareCosts.d1RowsRead / 1000000).toFixed(1)}M rows read
              </span>
              <span className="text-[11px] text-primary font-semibold font-mono">
                {formatMoney(cloudflareCosts.d1Cost)}
              </span>
            </div>

            <div className="p-3.5 rounded-xl border border-surface-border bg-muted/20 space-y-1">
              <span className="text-[10px] text-foreground-muted block">R2 Storage & Egress</span>
              <span className="font-mono font-bold text-foreground block">
                {cloudflareCosts.r2StorageGb} GB ($0 Egress)
              </span>
              <span className="text-[11px] text-primary font-semibold font-mono">
                {formatMoney(cloudflareCosts.r2Cost)}
              </span>
            </div>

            <div className="p-3.5 rounded-xl border border-surface-border bg-muted/20 space-y-1">
              <span className="text-[10px] text-foreground-muted block">Vectorize Search</span>
              <span className="font-mono font-bold text-foreground block">
                {(cloudflareCosts.vectorizeQueries / 1000).toFixed(0)}k queries
              </span>
              <span className="text-[11px] text-primary font-semibold font-mono">
                {formatMoney(cloudflareCosts.vectorizeCost)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Promotional Credits Burn & Liabilities Table */}
      <div className="p-6 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-4">
        <div className="flex items-center justify-between border-b border-surface-border pb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-500 flex items-center justify-center">
              <Gift className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-heading font-bold text-foreground">
                {t('admin.financial.tablePromoTitle')}
              </h3>
              <p className="text-[11px] text-foreground-muted">
                {t('admin.financial.kpiPromotionalSub')}
              </p>
            </div>
          </div>
          <div className="text-right">
            <span className="text-[10px] text-foreground-muted block">Total Promo Liability</span>
            <span className="font-mono text-sm font-bold text-amber-500">
              {formatMoney(summary?.promotionalCreditsDollarValue || 0)}
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead>
              <tr className="border-b border-surface-border text-foreground-muted font-medium text-[11px]">
                <th className="py-2.5 px-3">{t('admin.financial.colCategory')}</th>
                <th className="py-2.5 px-3">{t('admin.financial.colDescription')}</th>
                <th className="py-2.5 px-3 text-right">{t('admin.financial.colGrants')}</th>
                <th className="py-2.5 px-3 text-right">{t('admin.financial.colCredits')}</th>
                <th className="py-2.5 px-3 text-right">{t('admin.financial.colLiability')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border/50">
              {promotionalGrants.map((grant) => (
                <tr key={grant.category} className="hover:bg-muted/30 transition-colors">
                  <td className="py-2.5 px-3 font-mono font-semibold text-foreground">
                    {grant.category}
                  </td>
                  <td className="py-2.5 px-3 text-foreground-muted">{grant.description}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-foreground">{grant.count}</td>
                  <td className="py-2.5 px-3 text-right font-mono font-semibold text-primary">
                    +{grant.totalCredits.toLocaleString()}
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono font-bold text-amber-500">
                    {formatMoney(grant.estimatedCost)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
